<div class="pj-modal">
    <div class="pj-modal__header">
        <div>
            <h3>{{ isEdit ? 'Edit project' : 'Add project' }}</h3>
            <p class="pj-modal__sub">
                English is the published text. Any other language left blank falls
                back to it.
            </p>
        </div>
        <button type="button" class="pj-modal__close" (click)="activeModal.dismiss()" aria-label="Close">&times;</button>
    </div>

    <form class="pj-modal__body" (ngSubmit)="save()">
        <!-- One field column, switched by language. Driven by LanguageService,
             so a new language appears here on its own. -->
        <div class="lang-tabs" role="tablist">
            @for (lang of languages; track lang.code) {
                <button type="button" role="tab" [class.active]="activeLang === lang.code"
                    [attr.aria-selected]="activeLang === lang.code" (click)="activeLang = lang.code">
                    {{ lang.label }}
                    @if (!hasCopy(lang.code)) {
                        <span class="dot" [title]="'Nothing written in ' + lang.label + ' yet'">•</span>
                    }
                </button>
            }
        </div>

        @for (field of fields; track field.key) {
            <label class="field">
                <span>
                    {{ field.label }}
                    @if (field.required && isCanonical) { <em class="req">*</em> }
                </span>
                @if (field.long) {
                    <textarea [name]="activeLang + '-' + field.key" [(ngModel)]="copy[activeLang][field.key]"
                        rows="3" [placeholder]="placeholderFor(field)"
                        [class.invalid]="invalid(field.key)"></textarea>
                } @else {
                    <input type="text" [name]="activeLang + '-' + field.key"
                        [(ngModel)]="copy[activeLang][field.key]"
                        (ngModelChange)="field.key === 'title' ? onTitleChange() : null"
                        [placeholder]="placeholderFor(field)"
                        autocomplete="off" [class.invalid]="invalid(field.key)" />
                }
                @if (!isCanonical && canonicalValue(field.key)) {
                    <!-- What is being translated, so the tab is usable on its own. -->
                    <small class="source">EN: {{ canonicalValue(field.key) }}</small>
                }
            </label>
        }

        <!-- The page's address. Language-independent on purpose: the title is
             localised, so it cannot be the URL key. -->
        <label class="field">
            <span>URL slug <em class="req">*</em></span>
            <input type="text" name="slug" [(ngModel)]="slug" (input)="onSlugInput()"
                placeholder="greek-parliament" autocomplete="off" [class.invalid]="slugInvalid" />
            @if (slugInvalid) {
                <p class="err">
                    The slug needs at least one letter or number. It is the page's address, so it
                    has to be Latin characters even when the title is not.
                </p>
            } @else if (slugChanged) {
                <p class="warn">
                    This changes the project's public URL to <strong>/projects/{{ slugify(slug) }}</strong>.
                    Links to <strong>/projects/{{ project?.slug }}</strong> will stop working.
                </p>
            } @else {
                <small class="hint">Public address: /projects/{{ slugify(slug) || '…' }}</small>
            }
        </label>

        <div class="field-row">
            <label class="field">
                <span>Year</span>
                <input type="text" name="year" [(ngModel)]="year" placeholder="2023" autocomplete="off" />
            </label>
            <label class="field">
                <span>Display order</span>
                <input type="number" name="displayOrder" [(ngModel)]="displayOrder" min="1" placeholder="1"
                    autocomplete="off" />
                <!-- Says what the number will do: a taken position inserts here
                     and shifts the rest down, the same as dragging the row. -->
                @if (orderNote) {
                    <small class="hint order-note">{{ orderNote }}</small>
                }
            </label>
        </div>

        <div class="field">
            <span>
                Photo
                @if (!isEdit) { <em class="req">*</em> }
            </span>
            <!-- Click or drop. Previewed locally and uploaded to S3 by the
                 server when the form is saved. -->
            <div class="dropzone" [class.dragging]="dragging" [class.has-image]="!!previewUrl"
                [class.invalid]="imageMissing" (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)" (click)="fileInput.click()">
                @if (previewUrl) {
                    <img [src]="previewUrl" alt="" class="preview" />
                    <div class="dropzone__meta">
                        <span class="fname">{{ imageName || 'Current photo' }}</span>
                        @if (newImageFile) {
                            <button type="button" class="link-btn danger"
                                (click)="clearPicked(); $event.stopPropagation()">Undo</button>
                        }
                    </div>
                } @else {
                    <p class="dropzone__hint">
                        <strong>Drop an image here</strong> or click to choose<br />
                        png, jpg, webp or gif · up to 5MB
                    </p>
                }
            </div>
            <input #fileInput type="file" class="file-input" accept="image/png,image/jpeg,image/webp,image/gif"
                (change)="onFilePicked($event)" />
            @if (imageError) {
                <p class="err">{{ imageError }}</p>
            }
            @if (imageMissing) {
                <p class="err">A photo is required — the carousel has nothing to show without one.</p>
            }
            @if (isEdit && !project?.hasUploadedImage && project?.imageUrl) {
                <!-- Migrated rows point at an image that ships with the site, so
                     replacing it here moves that project onto S3. -->
                <small class="hint">Currently a bundled asset: <code>{{ project?.imageUrl }}</code></small>
            }
        </div>

        <button type="submit" class="pj-modal__submit">{{ isEdit ? 'Save changes' : 'Create' }}</button>
    </form>
</div>
